import { useCallback } from 'react'

import { ChatParticipant } from '@tribeplatform/gql-client/types'
import { useDemoteChatAdmins } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { T, useI18n } from '../../i18n/index.js'

import { ModalMemberDetails } from './ModalMemberDetails.js'

interface Props {
  open: boolean
  participant: ChatParticipant
  onClose: () => void
}

export const DemoteModal = ({ open, onClose, participant }: Props) => {
  const { mutateAsync: demoteChatAdmins, isLoading } = useDemoteChatAdmins()
  const { $t } = useI18n()

  const onSubmit = useCallback(async () => {
    try {
      await demoteChatAdmins({
        chatId: participant.chatId,
        input: {
          memberIds: [participant.memberId],
        },
      })
      toast({
        title: $t({
          id: 'Generics.Success',
          defaultMessage: 'Success',
        }),
        description: $t({
          id: 'Messaging.Groups.Demote.Success',
          defaultMessage: 'The admin has been demoted',
        }),
        status: 'success',
      })
      onClose()
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Messaging.Groups.Demote.Error',
          defaultMessage: 'Failed to demote admin',
        }),
        status: 'error',
      })
    }
  }, [$t, demoteChatAdmins, onClose, participant.chatId, participant.memberId])

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={
          <div className="flex items-center gap-2">
            <T
              defaultMessage="Demote {member_name} to member?"
              id="Messaging.DemoteModal.Title"
              values={{
                member_name: participant.name,
              }}
            />
          </div>
        }
        description={
          <T
            defaultMessage="{member_name} will no longer have admin privileges and control over the group."
            id="Messaging.DemoteModal.Description"
            values={{
              member_name: (
                <span className="font-bold">{participant.name}</span>
              ),
            }}
          />
        }
      />
      <Modal.Content>
        <ModalMemberDetails participant={participant} />
        <Modal.Actions className="!p-0">
          <Button destructive onClick={onSubmit} loading={isLoading}>
            <T defaultMessage="Demote" id="Generics.Demote" />
          </Button>
        </Modal.Actions>
      </Modal.Content>
    </Modal>
  )
}
